import { useId, useMemo, useRef, useState } from 'react'
import { Button, Dialog } from '@/components/common'
import { PrepackSurfaceLiveTag } from './PrepackSurfaceLiveTag'
import { removalImpact, removalSummary, removalSurfaceOptions } from './prepackSurfaceModel'

function SurfaceName({ option }) {
  return (
    <span className="min-w-0">
      <span className="block truncate font-medium">{option.name}{option.current ? ' (this surface)' : ''}</span>
      {option.known ? null : <span className="block truncate text-[10px] text-[var(--wf-color-text-muted)]">No longer a surface</span>}
    </span>
  )
}

function Choice({ name, value, checked, disabled, onChange, children, note }) {
  return (
    <label className={`flex items-start gap-2.5 text-xs ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <input type="radio" name={name} value={value} checked={checked} disabled={disabled} onChange={() => onChange(value)} className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="min-w-0">
        <span className="block font-medium">{children}</span>
        {note ? <span className="block text-[var(--wf-color-text-muted)]">{note}</span> : null}
      </span>
    </label>
  )
}

/**
 * Remove packs on the Surfaces page (#1090, after #999). The operator
 * removes the pack, or every pack its source schedule made in the pack's
 * regulation, from this surface, every surface those packs are on or chosen
 * ones (this surface always included: the action started here), and may
 * switch the schedule off on them so its new packs stay away too. Defaults
 * remove only this pack from this surface. A live summary counts the packs
 * (each once) for every choice.
 *
 * `target` is { pack, surface, options } with options from
 * packSurfaceOptions; `schedule` is the pack's schedule ({ name, deleted,
 * surfaces_off }), null for a hand-built pack, which hides the schedule-wide
 * choices; `preview` is the removal-preview query ({ data, isPending,
 * isError, refetch }). `onRemove(request)` sends { scope, surfaces,
 * stop_future, pack_updated_at?, request_id }; `error` is the last attempt's
 * failure, shown with Retry, which sends the choices shown now (the same
 * request id when they are unchanged). Removing is not deleting: the packs
 * stay listed under their schedule's Packs, or Schedules → Unscheduled packs
 * (#1080). `returnFocusRef` is the control that
 * opened it, and `returnFocusFallbackRef` where focus goes instead when the
 * removal took that control off the page (#1054).
 */
export function RemoveFromSurfacesDialog({ target, schedule, preview, busy, error, returnFocusRef, returnFocusFallbackRef, onRemove, onClose }) {
  const { pack } = target
  const currentSlug = target.surface || (target.options.find((option) => option.current) || target.options[0])?.slug
  const [packScope, setPackScope] = useState('pack')
  const [surfaceScope, setSurfaceScope] = useState('current')
  const [chosen, setChosen] = useState(() => new Set([currentSlug]))
  const [stopFuture, setStopFuture] = useState(false)
  // The id of the last request sent and the choices it was sent for.
  const attempt = useRef(null)
  const cancelRef = useRef(null)
  const groupName = useId()

  const previewData = preview?.data || null
  const scheduleWide = packScope === 'schedule'
  const options = useMemo(
    () => (scheduleWide && previewData ? removalSurfaceOptions(previewData, currentSlug) : target.options),
    [scheduleWide, previewData, currentSlug, target.options],
  )
  // Choices on surfaces the packs in scope are not on drop out.
  const chosenHere = [...chosen].filter((slug) => options.some((option) => option.slug === slug))
  const impact = removalImpact({ preview: previewData, pack, packScope, surfaceScope, chosen: chosenHere, currentSlug })
  const canStopFuture = Boolean(schedule && !schedule.deleted)
  const alreadyOff = new Set(schedule?.surfaces_off || [])
  const everyOff = impact.surfaces.length > 0 && impact.surfaces.every((slug) => alreadyOff.has(slug))
  const sendsStop = canStopFuture && stopFuture && !everyOff
  const summary = removalSummary(impact, { options, schedule, packScope, surfaceScope, stopFuture: sendsStop })
  // "All packs from this schedule (n)" counts them on the surfaces chosen now.
  const scheduleChosen = previewData
    ? [...chosen].filter((slug) => removalSurfaceOptions(previewData, currentSlug).some((option) => option.slug === slug))
    : []
  const scheduleCount = previewData
    ? removalImpact({ preview: previewData, pack, packScope: 'schedule', surfaceScope, chosen: scheduleChosen, currentSlug }).count
    : null
  const previewFailed = Boolean(preview?.isError) && !previewData

  const toggle = (slug) => setChosen((set) => {
    const next = new Set(set)
    if (next.has(slug)) next.delete(slug)
    else next.add(slug)
    return next
  })
  // Sends the choices shown now. The id names the action: Retry with the same
  // choices repeats it (the server answers what the first did if it went
  // through), changed choices get a new one.
  const remove = () => {
    const body = {
      scope: scheduleWide ? 'schedule' : 'pack',
      surfaces: impact.surfaces,
      stop_future: sendsStop,
      ...(scheduleWide ? {} : { pack_updated_at: pack.updated_at }),
    }
    const key = JSON.stringify([body.scope, [...body.surfaces].sort(), body.stop_future])
    const id = attempt.current?.key === key ? attempt.current.id : crypto.randomUUID()
    attempt.current = { id, key }
    onRemove({ ...body, request_id: id })
  }
  const nothingToRemove = impact.count === 0 || (scheduleWide && Boolean(preview?.isPending))
  const dialogProps = { open: true, onClose, dismissable: !busy, hideCloseButton: busy, returnFocusRef, returnFocusFallbackRef }
  const scheduleName = schedule?.name || previewData?.schedule?.name

  return (
    <Dialog
      {...dialogProps}
      title="Remove packs"
      description={`Selected: ${pack.title}${schedule ? ` · Schedule: ${scheduleName || 'Loading…'}` : ''}`}
      initialFocusRef={cancelRef}
      footer={(
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={remove} disabled={nothingToRemove} loading={busy}>
            {`Remove ${impact.count} pack${impact.count === 1 ? '' : 's'}`}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-4">
        {schedule ? (
          <fieldset className="flex flex-col gap-2" disabled={busy}>
            <legend className="mb-1 text-xs font-semibold">Which packs?</legend>
            <Choice name={`${groupName}-packs`} value="pack" checked={packScope === 'pack'} onChange={setPackScope}>Only this pack</Choice>
            <Choice
              name={`${groupName}-packs`}
              value="schedule"
              checked={packScope === 'schedule'}
              disabled={previewFailed}
              onChange={setPackScope}
              note={previewFailed ? 'Couldn’t load the schedule’s packs.' : null}
            >
              All packs from this schedule{preview?.isPending ? ' (counting…)' : scheduleCount != null ? ` (${scheduleCount}${previewData?.truncated ? '+' : ''})` : ''}
            </Choice>
            {previewFailed ? (
              <Button size="compact" variant="ghost" className="self-start" onClick={() => preview.refetch?.()}>Retry loading packs</Button>
            ) : null}
          </fieldset>
        ) : null}

        <fieldset className="flex flex-col gap-2" disabled={busy}>
          <legend className="mb-1 text-xs font-semibold">From where?</legend>
          <Choice name={`${groupName}-surfaces`} value="current" checked={surfaceScope === 'current'} onChange={setSurfaceScope}>Current surface only</Choice>
          <Choice name={`${groupName}-surfaces`} value="all" checked={surfaceScope === 'all'} onChange={setSurfaceScope}>All surfaces</Choice>
          <Choice name={`${groupName}-surfaces`} value="choose" checked={surfaceScope === 'choose'} onChange={setSurfaceScope}>Choose surfaces…</Choice>
        </fieldset>

        {surfaceScope === 'choose' ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-xs">
              <button type="button" disabled={busy || options.every((option) => chosen.has(option.slug))} onClick={() => setChosen(new Set(options.map((option) => option.slug)))} className="text-[var(--wf-color-action-text)] hover:underline disabled:opacity-40 disabled:no-underline">Select all</button>
              <span aria-hidden="true" className="text-[var(--wf-color-text-muted)]">·</span>
              <button type="button" disabled={busy || impact.surfaces.length === 1} onClick={() => setChosen(new Set([currentSlug]))} className="text-[var(--wf-color-action-text)] hover:underline disabled:opacity-40 disabled:no-underline">Only {options.find((option) => option.current)?.name || currentSlug}</button>
            </div>
            <ul aria-label="Surfaces" className="flex max-h-[240px] flex-col gap-1 overflow-y-auto text-xs">
              {options.map((option) => {
                const checked = option.current || chosen.has(option.slug)
                return (
                  <li key={option.slug}>
                    <label className={`flex items-center justify-between gap-2 rounded border px-3 py-2 ${checked ? 'border-[var(--wf-color-action)]' : 'border-[var(--wf-color-border)]'} ${option.current || busy ? '' : 'cursor-pointer'}`}>
                      <span className="flex min-w-0 items-center gap-2.5">
                        <input type="checkbox" checked={checked} disabled={busy || option.current} onChange={() => toggle(option.slug)} className="h-4 w-4 shrink-0" />
                        <SurfaceName option={option} />
                      </span>
                      <PrepackSurfaceLiveTag live={option.live} />
                    </label>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}

        {canStopFuture ? (
          <label className={`flex items-start gap-2.5 text-xs ${everyOff || busy ? 'opacity-60' : 'cursor-pointer'}`}>
            <input type="checkbox" checked={stopFuture && !everyOff} disabled={busy || everyOff} onChange={(event) => setStopFuture(event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0">
              <span className="block font-medium">Stop future packs from this schedule on the selected surfaces.</span>
              <span className="block text-[var(--wf-color-text-muted)]">
                {everyOff ? 'The schedule is already switched off on the selected surfaces.' : 'The schedule keeps running on its other surfaces. Switch it back on in Connected schedules.'}
              </span>
            </span>
          </label>
        ) : null}

        <p role="status" aria-live="polite" className="rounded border border-[var(--wf-color-border)] px-3 py-2 text-xs">{summary}</p>

        {error ? (
          <div role="alert" className="flex items-center justify-between gap-2 rounded border border-[var(--wf-color-danger)] px-3 py-2 text-xs text-[var(--wf-color-danger)]">
            <span className="min-w-0">{error.message}</span>
            <Button size="compact" variant="secondary" disabled={busy || nothingToRemove} onClick={remove}>Retry</Button>
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
